<template>
    <div class="pa-5 compact-form" v-if="activeWorkOrderGroup">
        <v-row>
            <v-col class="col-item mt-2 row-title" cols="3">{{ t("Production order number") }}</v-col>
            <v-col class="col-item" cols="6">
                <v-text-field
                    :value="
                        activeWorkOrderGroup.production_order_number ? activeWorkOrderGroup.production_order_number : ''
                    "
                    @input="updateField($event, 'production_order_number')"
                    class="text-field-item mb-1"
                    hide-details
                    dense
                    outlined
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-item mt-2 row-title" cols="3">{{ t("Production batch number") }}</v-col>
            <v-col class="col-item" cols="6">
                <v-text-field
                    :value="
                        activeWorkOrderGroup.production_order_number ? activeWorkOrderGroup.production_batch_number : ''
                    "
                    @input="updateField($event, 'production_batch_number')"
                    class="text-field-item mb-1"
                    hide-details
                    dense
                    outlined
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-item mt-2 row-title" cols="3">{{ t("Sales order number") }}</v-col>
            <v-col class="col-item" cols="6">
                <v-text-field
                    :value="activeWorkOrderGroup.sales_order_number ? activeWorkOrderGroup.sales_order_number : ''"
                    @input="updateField($event, 'sales_order_number')"
                    class="text-field-item mb-1"
                    hide-details
                    dense
                    outlined
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-item mt-2 row-title" cols="3">{{ t("Estimated production time") }} (MM:ss)</v-col>
            <v-col class="col-item" cols="6">
                <v-text-field
                    :value="activeWorkOrderGroup.estimated_time || ''"
                    @input="updateField($event, 'estimated_time')"
                    class="text-field-item mb-1"
                    hide-details
                    dense
                    outlined
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-item mt-2 row-title" cols="3">{{ t("Production date") }}</v-col>
            <v-col class="col-item" cols="6">
                <v-menu
                    ref="productionDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            outlined
                            dense
                            :value="productionDateValue"
                            readonly
                            hide-details
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                        v-model="productionDate"
                        mode="date"
                        first-day-of-week="1"
                        @input="$refs.productionDateMenu.isActive = false"
                    ></v-date-picker>
                </v-menu>
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-item mt-2 row-title" cols="3">{{ t("Production start") }}</v-col>
            <v-col class="col-item" cols="6">
                <v-text-field
                    :value="activeWorkOrderGroup.production_time"
                    @input="updateField($event, 'production_time')"
                    dense
                    hide-details
                    flat
                    pattern="^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(:[0-5][0-9])?$"
                    type="time"
                    outlined
                >
                </v-text-field>
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-item mt-2 row-title" cols="3">{{ t("Product name") }}</v-col>
            <v-col class="col-item" cols="6">
                <v-autocomplete
                    :items="products"
                    :value="activeWorkOrderGroup.product_name ? activeWorkOrderGroup.product_name : ''"
                    @input="updateWorkOrderGroupProduct($event)"
                    :item-text="item => `[${item.code}] ${item.name}`"
                    item-value="name"
                    id="searchInput"
                    :placeholder="t('Name')"
                    flat
                    outlined
                    hide-details
                    dense
                >
                </v-autocomplete>
            </v-col>
        </v-row>

        <v-row>
            <v-col class="col-item mt-2 row-title" cols="3">{{ t("Planned quantity") }}</v-col>
            <v-col class="col-item" cols="6">
                <v-text-field
                    :value="activeWorkOrderGroup.planned_quantity ? activeWorkOrderGroup.planned_quantity : 0"
                    @input="updateField($event, 'planned_quantity')"
                    class="text-field-item mb-1"
                    @keypress="onlyIntegersWithComma"
                    hide-details
                    dense
                    outlined
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-item row-title" cols="3">{{ t("Product code") }}</v-col>
            <v-col class="col-item mt-0" cols="6">
                {{ activeWorkOrderGroup.product_code ? activeWorkOrderGroup.product_code : "" }}
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-item row-title mt-1" cols="3">{{ t("Product EAN") }}</v-col>
            <v-col class="col-item mt-1" cols="6">
                {{ activeWorkOrderGroup.product_ean ? activeWorkOrderGroup.product_ean : "" }}
            </v-col>
        </v-row>
    </div>
</template>
<script>
import { mapActions, mapMutations, mapState } from "vuex";
import Store from "@/store";
import moment from "moment/moment";

export default {
    name: "group-info-edit-tab",

    data() {
        return { productionDate: "-" };
    },
    async mounted() {
        this.productionDate = this.getProductionDate();
    },
    watch: {
        productionDate() {
            if (this.productionDate !== "-") {
                this.updateProductionDateTime("date");
            }
        }
    },
    computed: {
        ...mapState("WorkOrderStore", ["activeWorkOrderGroup", "products"]),
        productionDateValue() {
            return this.getProductionDate();
        },
        estimatedTime() {
            return this.getEstimationTime(this.activeWorkOrderGroup.estimated_time);
        }
    },
    methods: {
        ...mapMutations("WorkOrderStore", [
            "updateWorkOrderGroupProperty",
            "updateActiveWorkOrderGroupProductProperty"
        ]),
        updateField(value, field) {
            if (["planned_quantity"].includes(field) && value) {
                value = value.replace(",", ".");
            }

            this.updateWorkOrderGroupProperty({ prop: field, value: value });
        },
        updateWorkOrderGroupProduct(product_name) {
            this.updateActiveWorkOrderGroupProductProperty({ product_name });
        },
        getProductionDate() {
            if (!this.activeWorkOrderGroup.production_date) {
                return moment().format("DD/MM/YYYY");
            }

            return moment(this.activeWorkOrderGroup.production_date).format("DD/MM/YYYY");
        },
        updateProductionDateTime(dateTimePart) {
            const newDateTime = new Date(this.activeWorkOrderGroup.production_date);
            let month = (newDateTime.getMonth() + 1).toString().padStart(2, "0");
            const formattedDateTimeString = `${newDateTime.getFullYear()}-${month}-${newDateTime.getDate()} ${newDateTime.getHours()}:${newDateTime.getMinutes()}:00`;
            let newFormattedDateTimeString;
            if (dateTimePart === "date") {
                let newDateString;
                if (this.productionDate.includes("/")) {
                    newDateString = this.productionDate
                        .split("/")
                        .reverse()
                        .join("-");
                } else {
                    newDateString = this.productionDate;
                }
                const parts = formattedDateTimeString.split(" ");
                newFormattedDateTimeString = `${newDateString} ${parts[1]}`;
                this.productionDate = newDateString;
            }

            this.updateField(newFormattedDateTimeString, "production_date");
        },
        getEstimationTime(est_time) {
            if (!est_time) {
                return "-";
            }
            let time = moment(est_time, "HH:mm:ss");
            const hours = time.hours();
            const minutes = time.minutes();
            const seconds = time.seconds();

            return `${hours * 60 + minutes}:${seconds.toString().padStart(2, "0")}`;
        },
        onlyIntegersWithComma($event) {
            let keyCode = $event.keyCode ? $event.keyCode : $event.key;
            if (keyCode < 44 || keyCode === 47 || keyCode === 45 || keyCode > 57) {
                $event.preventDefault();
            }
        }
    }
};
</script>

<style scoped>
.row-title {
    color: gray;
}

.col-item {
    padding-top: 8px;
    padding-bottom: 8px;
}
</style>
